import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { useReport, downloadCsv, RANGES, todayIst, daysAgo } from "../lib/reports";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHeader, Panel, btnGhost, Empty } from "../components/Panel";
import { PnlReport } from "../components/reports/PnlReport";
import { SkeletonRows } from "../components/motion";
import { TradebookReport, LedgerReport } from "../components/reports/TradeLedger";

const TABS = [["pnl", "P&L", "/reports/pnl"], ["tradebook", "Tradebook", "/reports/tradebook"], ["ledger", "Ledger", "/reports/ledger"]];
const SEGMENTS = [["ALL", "All segments"], ["EQ", "Equity"], ["FO", "F&O"], ["COM", "Commodity"]];
const LEDGER_TYPES = [["ALL", "All entries"], ["CAPITAL", "Capital"], ["REALISED_PNL", "Realised P&L"], ["CHARGES", "Charges"], ["DELIVERY_BUY", "Delivery buy"], ["DELIVERY_SELL", "Delivery sell"], ["MTM_SETTLEMENT", "MTM settlement"], ["EXPIRY_SETTLEMENT", "Expiry settlement"]];
const dateCls = "h-8 px-2 rounded-md border border-input bg-background num text-[12px] outline-none focus:border-primary dark:[color-scheme:dark]";

function Filters({ f, set, tab }) {
  const opts = tab === "ledger" ? LEDGER_TYPES : SEGMENTS;
  const key = tab === "ledger" ? "type" : "segment";
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 border-b border-border" data-testid="report-filters">
      {RANGES.map(([l, n]) => (
        <button key={l} data-testid={`report-range-${l}`} onClick={() => set({ start: daysAgo(n), end: todayIst() })}
          className={`${btnGhost} ${f.start === daysAgo(n) && f.end === todayIst() ? "border-primary text-primary" : ""}`}>{l}</button>))}
      <input type="date" data-testid="report-start-date" value={f.start} max={f.end} onChange={(e) => e.target.value && set({ start: e.target.value })} className={dateCls} />
      <span className="text-muted-foreground text-[11px]">to</span>
      <input type="date" data-testid="report-end-date" value={f.end} min={f.start} max={todayIst()} onChange={(e) => e.target.value && set({ end: e.target.value })} className={dateCls} />
      <Select value={f[key]} onValueChange={(v) => set({ [key]: v })}>
        <SelectTrigger className="h-8 w-40 text-[11.5px]" data-testid="report-segment-filter"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-popover">{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function Body({ tab, state, filtered }) {
  if (state.error) return <Panel><Empty testid="report-error">{state.error}</Empty></Panel>;
  if (!state.data) return <Panel><SkeletonRows rows={8} cols={8} testid="report-skeleton" /></Panel>;
  if (tab === "pnl") return <PnlReport data={state.data} />;
  if (tab === "tradebook") return <TradebookReport rows={state.data} />;
  return <LedgerReport data={state.data} filtered={filtered} />;
}

export default function Reports() {
  const [tab, setTab] = useState("pnl");
  const [f, setF] = useState({ start: daysAgo(30), end: todayIst(), segment: "ALL", type: "ALL" });
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const path = TABS.find((t) => t[0] === tab)[2];
  const params = tab === "ledger" ? { start: f.start, end: f.end, type: f.type } : { start: f.start, end: f.end, segment: f.segment };
  const state = useReport(path, params);
  const [busy, setBusy] = useState(false);
  const download = async () => { setBusy(true); await downloadCsv(path, params); setBusy(false); };
  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="reports-page">
      <PageHeader title="Reports">
        <span data-testid="reports-sim-label" className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-amber-600 dark:text-amber-400">SIMULATED – not a real trade record</span>
        <button data-testid="report-download-csv" onClick={download} disabled={busy}
          className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-[12px] font-semibold flex items-center gap-1.5 hover:brightness-110 disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}Download CSV</button>
      </PageHeader>
      <Panel>
        <div className="flex items-center gap-1 px-3 border-b border-border">
          {TABS.map(([v, l]) => (
            <button key={v} data-testid={`report-tab-${v}`} onClick={() => setTab(v)}
              className={`h-9 px-3 text-[12.5px] border-b-2 transition-colors duration-150 ${tab === v ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{l}</button>))}
          {state.loading && state.data && <Loader2 className="ml-2 h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>
        <Filters f={f} set={set} tab={tab} />
      </Panel>
      <Body key={tab} tab={tab} state={state} filtered={f.type !== "ALL"} />
      <p className="text-[10.5px] text-muted-foreground">P&L is computed from your trades (average-cost method). Not a contract note or tax statement.</p>
    </div>
  );
}
